// 🆕 Feb 2026 — Overlay flottante CENTRATO che mostra le barre di avanzamento dei download
// e delle estrazioni allegati in corso. Ascolta il pub/sub globale in `lib/downloadWithProgress.js`.
import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Loader2, Download, Upload, AlertCircle, CheckCircle2, FileText } from "lucide-react";
import { subscribeDownloadProgress } from "@/lib/downloadWithProgress";

const formatBytes = (n) => {
  if (n == null || Number.isNaN(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const phaseMeta = {
  downloading: { icon: Download, label: "Download in corso", color: "text-cyan-600" },
  uploading: { icon: Upload, label: "Upload in corso", color: "text-blue-600" },
  extracting: { icon: FileText, label: "Estrazione in corso", color: "text-indigo-600" },
  done: { icon: CheckCircle2, label: "Completato", color: "text-emerald-600" },
  error: { icon: AlertCircle, label: "Errore", color: "text-red-600" },
};

export default function DownloadProgressOverlay() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => subscribeDownloadProgress(setJobs), []);

  if (!jobs.length) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none px-4"
      data-testid="download-progress-overlay"
    >
      <div className="flex flex-col gap-2 w-full max-w-md pointer-events-auto">
        {jobs.map((job) => {
          const meta = phaseMeta[job.phase] || phaseMeta.downloading;
          const Icon = meta.icon;
          const showPct = job.percent != null && job.phase !== "error";
          return (
            <div
              key={job.id}
              className="bg-white border border-slate-200 rounded-lg shadow-2xl p-4 animate-in zoom-in-95 fade-in duration-200"
              data-testid={`download-progress-item-${job.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`shrink-0 mt-0.5 ${meta.color}`}>
                  {job.phase === "downloading" || job.phase === "uploading" || job.phase === "extracting" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate" title={job.label}>
                    {job.label}
                  </p>
                  <p className={`text-xs ${meta.color}`}>
                    {job.phase === "error"
                      ? job.message || "Errore"
                      : `${meta.label}${showPct ? ` · ${job.percent}%` : job.loaded ? ` · ${formatBytes(job.loaded)}` : "…"}`}
                  </p>
                </div>
                {showPct && (
                  <span
                    className="text-sm font-semibold text-slate-700 tabular-nums shrink-0"
                    data-testid={`download-progress-pct-${job.id}`}
                  >
                    {job.percent}%
                  </span>
                )}
              </div>
              {job.phase !== "error" && (
                <Progress
                  value={job.percent ?? undefined}
                  className="mt-3 h-2"
                  data-testid={`download-progress-bar-${job.id}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
